import { useTranslation } from 'react-i18next';
import { NavigationSidebar, useAuth, type SidebarLink } from '@monecolier/ui';
import { LanguageSwitcher } from './LanguageSwitcher';
import { MAIN_NAV, NAV } from '../lib/navigation';

/**
 * UX-17 — patron de navigation UNIFIÉ web-admin/web-parent : REMPLACE
 * `BottomTabBar.tsx` (UX-11, onglets fixes en bas d'écran), qui ne
 * correspondait pas à la référence de design system fournie par le PO ("un
 * seul patron de menu... sidebar fixe à gauche en desktop, tiroir hors-écran
 * en mobile"). Wrapper PROPRE à web-parent au-dessus du composant partagé
 * `@monecolier/ui#NavigationSidebar` (même exact mécanisme que
 * `apps/web-admin/src/components/Sidebar.tsx`, voir ce fichier).
 *
 * ⚠️ **UX-32 — web-parent N'UTILISE PLUS AUCUN `SidebarGroup` depuis ce
 * ticket, contrairement à `web-admin`** (qui garde les siens — plusieurs
 * rôles/`Membership`, vraie hiérarchie nécessaire, voir son propre
 * `Sidebar.tsx`) : les 4 tickets précédents (UX-28/UX-30/UX-31) avaient
 * chacun vidé un groupe DEVENU redondant (un seul sous-item, en-tête +
 * sous-lien superflus) vers `extraLinks` — "Demandes" restait le SEUL
 * groupe encore légitime au sens strict (3 items réels, jamais un groupe
 * à une seule entrée). La référence Claude Design du 23/08 confirme
 * pourtant une Sidebar ENTIÈREMENT PLATE côté web-parent, sans AUCUN
 * en-tête de section visible — décision de refonte assumée, pas la
 * correction d'un anti-pattern comme les 3 tickets précédents : `web-parent`
 * n'a structurellement AUCUNE notion de rôle (pas de `Membership` — un
 * compte parent est homogène), la hiérarchie "groupe de domaine" n'y a
 * donc jamais eu la même justification que côté `web-admin`.
 *
 * ⚠️ **UX-38 — "Mes enfants"/"Nouvelle demande"/"Réinscription" (3 liens
 * distincts) FUSIONNÉS en UN SEUL, "Inscriptions" (`/inscriptions`)** — les
 * trois pages qu'ils ciblaient (`Children.tsx`/`Enroll.tsx`/`Reenroll.tsx`)
 * sont retirées, remplacées par une page unique de gestion complète
 * (`Inscriptions.tsx`), voir ce fichier pour le détail de la décision
 * (Option B, gardée avec `Home.tsx`).
 *
 * Les liens ci-dessous (hors `homeLink`, toujours en tout premier) sont
 * TOUS des `extraLinks`, dans l'ORDRE EXACT de la référence Claude Design du
 * 24/08 : Inscriptions (`/inscriptions`, UX-38), Paiements (`/payments`,
 * aperçu agrégé PAR-05, liste unifiée scolarité+fournitures depuis
 * UX-26 — `/supplies`/FOU-02 reste redirigé vers `/payments`, `App.tsx`,
 * inchangé), Documents (`/documents`, hub ENR-07), Réglages (`/settings`,
 * page hub vers `/notification-preferences`/NOT-07 et `/data-requests`/
 * CMP-03, UX-27).
 *
 * ⚠️ **ATT-03 — "Présences" (`/attendance`) AJOUTÉ entre Paiements et
 * Documents** : nouvel onglet DÉDIÉ (pas intégré à une fiche enfant
 * existante, décision produit du 23/09) pour la consultation LECTURE SEULE
 * des présences par créneau — voir `pages/Attendance.tsx`.
 *
 * EVA-07 — "Notes" (`/grades`) AJOUTÉ juste après Présences, même décision
 * (onglet dédié, lecture seule) — voir `pages/Grades.tsx` et `NAV.grades`.
 *
 * `/students/:studentId/financial-overview` et
 * `/enrollments/:enrollmentId/documents` restent volontairement HORS
 * sidebar (routes scopées à un enfant/une inscription précise, jamais des
 * destinations de navigation générale — atteintes depuis les cartes de
 * `Home.tsx` ou les hubs `/payments`/`/documents` eux-mêmes, comme avant ce
 * ticket).
 */
// UX-21 : icônes emoji DÉCORATIVES (aria-hidden, voir Sidebar.tsx,
// packages/ui) — même mapping cohérent que web-admin pour les concepts
// partagés (📋 Inscriptions, 💰 Paiements, 📄 Documents, ⚙️ Réglages) — voir
// docs/architecture.md, section "UX-21", pour le mapping des DEUX apps.
// ⚠️ 👪/📝/🕐 (Mes enfants/Nouvelle demande/Réinscription) RETIRÉS depuis
// UX-38 — fusionnés sous 📋 Inscriptions. 📦 (Fournitures) RETIRÉ depuis
// UX-26 — plus d'entrée dédiée.
// PAR-15 : `brandLabel`/`homeLink.label` restent codés en dur — "Mon Ecolier"
// est un NOM DE MARQUE (jamais traduit, décision FND-56), "Accueil" est
// traduit via `t('sidebar.home')` comme les 4 autres labels ci-dessous.
export function Sidebar() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  // UX-28/UX-30/UX-31/UX-32/UX-38 : TOUS les liens gardés sous la MÊME garde
  // `isAuthenticated` (jamais affichés à un visiteur anonyme) — `web-parent`
  // n'a plus AUCUN `SidebarGroup` depuis UX-32, voir le doc-comment de tête
  // pour la justification complète de cette refonte.
  // UX-85 : entrées définies dans lib/navigation.ts, partagées avec le fil d'Ariane.
  const extraLinks: SidebarLink[] = isAuthenticated
    ? MAIN_NAV.map((item) => ({ to: item.to, label: t(item.labelKey), icon: item.icon }))
    : [];

  return (
    <NavigationSidebar
      brandLabel="Mon Ecolier"
      homeLink={{ to: NAV.home.to, label: t(NAV.home.labelKey), icon: NAV.home.icon }}
      extraLinks={extraLinks}
      groups={[]}
      // PAR-16 : toujours transmis, plus conditionné à `isAuthenticated` —
      // `NavigationSidebar` (`@monecolier/ui`) ne le garde plus lui-même par
      // l'authentification (voir son propre commentaire), c'est ce qui rend
      // le sélecteur de langue visible sur Login/Register/écrans
      // d'invitation/l'accueil public, pas seulement une fois connecté.
      footerExtra={<LanguageSwitcher />}
      // UX-84 — textes propres au composant partagé (défaut français en dur
      // côté `packages/ui`), traduits ici comme les liens ci-dessus.
      logoutLabel={t('sidebar.logout')}
      mainNavLabel={t('sidebar.mainNav')}
      openMenuLabel={t('sidebar.openMenu')}
      closeMenuLabel={t('sidebar.closeMenu')}
      drawerLabel={t('sidebar.drawer')}
    />
  );
}
